import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../auth/AuthContext';
import apiClient from '../services/api';
import { PageContentWrapper, PageHeader, PageDescription } from '../main/LayoutStyles';
import { Card, StyledButton, AlertBox } from '../main/CommonStyles';
import { Loader2, AlertTriangle, ChevronDown, ChevronUp, Users, BookUser, PlusCircle, Search } from 'lucide-react';
import MemberList from './MemberList';
import InviteForm from './InviteForm';
import GenerateCodes from './GenerateCodes';

const DashboardLayout = styled.div`
    width: 100%;
`;

const ColumnsWrapper = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 1.5rem;
    margin-top: 1rem;

    @media (min-width: 1024px) {
        grid-template-columns: 1fr 1fr;
        align-items: start;
    }
`;

const SectionCard = styled(Card)`
    padding: 1.5rem;
    height: 100%;
    margin-top: 1.5rem;
`;

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding-bottom: 0.75rem;
    margin-bottom: 1rem;
    border-bottom: 1px solid ${({ theme }) => theme.colors.borderColor};

    h3 {
        margin: 0;
        font-size: 1.25rem;
        color: ${({ theme }) => theme.colors.textDark};
        display: flex;
        align-items: center;
        gap: 0.75rem;
    }
    svg {
        color: ${({ theme }) => theme.colors.primary};
    }
`;

const AccordionHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem 1rem;
    background-color: #f8fafc;
    border: 1px solid ${({ theme, $isHighlighted }) => $isHighlighted ? theme.colors.secondary : '#e2e8f0'};
    border-width: ${({ $isHighlighted }) => $isHighlighted ? '2px' : '1px'};
    cursor: pointer;
    border-radius: ${({ isOpen }) => (isOpen ? '0.5rem 0.5rem 0 0' : '0.5rem')};
    transition: all 0.2s ease-in-out;

    &:hover {
        background-color: #f1f5f9;
    }
`;

const AccordionContent = styled.div`
    padding: 0;
    border: 1px solid #e2e8f0;
    border-top: none;
    border-radius: 0 0 0.5rem 0.5rem;
    background-color: #fff;
`;

const CodeSearch = styled.div`
    margin-bottom: 1rem;
    form {
        display: flex;
        gap: 0.5rem;
    }
    input {
        flex-grow: 1;
        padding: 0.5rem;
        border: 1px solid #ccc;
        border-radius: 8px;
    }
`;

const AccordionItem = ({ group, children, forceOpen, highlight }) => {
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        if (forceOpen) {
            setIsOpen(true);
        }
    }, [forceOpen]);

    return (
        <div>
            <AccordionHeader isOpen={isOpen} onClick={() => setIsOpen(!isOpen)} $isHighlighted={highlight}>
                <div style={{display: 'flex', alignItems: 'center', gap: '0.75rem'}}>
                    <BookUser />
                    <span style={{fontWeight: '600'}}>{group.name}</span>
                </div>
                {isOpen ? <ChevronUp /> : <ChevronDown />}
            </AccordionHeader>
            {isOpen && <AccordionContent>{children}</AccordionContent>}
        </div>
    );
};

const AdminDashboard = () => {
    const { currentUser, isAdmin, isSystemAdmin } = useAuth();
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [showGroupForm, setShowGroupForm] = useState(false);
    const [newGroupName, setNewGroupName] = useState('');
    const [isCreatingGroup, setIsCreatingGroup] = useState(false);

    const [searchCode, setSearchCode] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [searchedGroupId, setSearchedGroupId] = useState(null);

    useEffect(() => {
        const orgId = currentUser?.organization?.id;
        if (orgId) {
            setLoading(true);
            apiClient.get('/admin/groups')
                .then(res => setGroups(res.data))
                .catch(() => setError('Nie udało się załadować grup.'))
                .finally(() => setLoading(false));
        } else if (currentUser) {
            setLoading(false);
        }
    }, [currentUser?.organization?.id, refreshTrigger]);

    const handleStaffInvited = () => {
        setRefreshTrigger(prev => prev + 1);
    };

    const handleCreateGroup = async (e) => {
        e.preventDefault();
        if (!newGroupName.trim()) return;
        setIsCreatingGroup(true);
        setError(''); setSuccess('');
        try {
            const response = await apiClient.post('/admin/groups', { groupName: newGroupName });
            setGroups([...groups, response.data]);
            setSuccess(`Klasa "${newGroupName}" została pomyślnie utworzona.`);
            setNewGroupName('');
            setShowGroupForm(false);
        } catch (err) {
            setError('Nie udało się utworzyć grupy. Spróbuj ponownie.');
        } finally {
            setIsCreatingGroup(false);
        }
    };

    const handleSearchByCode = async (e) => {
        e.preventDefault();
        if (!searchCode.trim()) return;
        setIsSearching(true);
        setSearchedGroupId(null);
        setError(''); setSuccess('');
        try {
            const response = await apiClient.get(`/admin/find-group-by-code/${searchCode}`);
            setSearchedGroupId(response.data.groupId);
            setSuccess("Znaleziono ucznia i rozwinięto jego klasę.");
        } catch (err) {
            setError(err.response?.data?.message || "Nie znaleziono ucznia z tym kodem.");
        } finally {
            setIsSearching(false);
        }
    };

    const staffRoles = {
        "ROLE_SCHOOL_STAFF": "Nauczyciel / Personel",
        "ROLE_PSYCHOLOGIST": "Psycholog Szkolny",
        "ROLE_PARENT": "Rodzic / Opiekun"
    };

    if (loading) {
        return <PageContentWrapper><div style={{textAlign: 'center'}}><Loader2 className="icon-spin" /> Ładowanie danych...</div></PageContentWrapper>;
    }

    return (
        <PageContentWrapper className="custom-scrollbar">
            <DashboardLayout>
                <PageHeader>Panel Administratora Organizacji</PageHeader>
                <PageDescription>
                    Witaj, {currentUser?.username}! Zarządzasz organizacją: <strong>{currentUser?.organization?.name || 'Brak przypisanej organizacji'}</strong>.
                </PageDescription>

                {error && <AlertBox type="error" style={{marginTop: '1rem'}}>{error}</AlertBox>}
                {success && <AlertBox type="success" style={{marginTop: '1rem'}}>{success}</AlertBox>}

                <ColumnsWrapper>
                    <SectionCard>
                        <SectionHeader>
                            <h3><Users size={24} />Personel</h3>
                        </SectionHeader>

                        {currentUser?.organization?.id ? (
                            <InviteForm
                                title="Zaproś nowego członka personelu"
                                description="Osoba zaproszona zostanie automatycznie przypisana do Twojej organizacji."
                                organizationId={currentUser.organization.id}
                                availableRoles={staffRoles}
                                onStaffInvited={handleStaffInvited}
                                successMessage="Zaproszenie zostało wysłane."
                            />
                        ) : (
                            <AlertBox type="info" style={{marginTop: '1.5rem'}}>Twoje konto nie jest przypisane do organizacji.</AlertBox>
                        )}

                        <MemberList organizationId={currentUser?.organization?.id} keyForRefresh={refreshTrigger} />
                    </SectionCard>

                    {isAdmin && !isSystemAdmin && (
                        <SectionCard>
                            <SectionHeader>
                                <h3><BookUser size={24} />Klasy i Kody Dostępu</h3>
                                {!showGroupForm && (
                                    <StyledButton $variant="outline" onClick={() => setShowGroupForm(true)}>
                                        <PlusCircle size={16}/> Dodaj Klasę
                                    </StyledButton>
                                )}
                            </SectionHeader>

                            {showGroupForm && (
                                <form onSubmit={handleCreateGroup} style={{ marginBottom: '1.5rem' }}>
                                    <input
                                        type="text"
                                        value={newGroupName}
                                        onChange={(e) => setNewGroupName(e.target.value)}
                                        placeholder="Wpisz nazwę nowej klasy"
                                        required
                                        style={{ width: '100%', padding: '0.75rem', marginBottom: '1rem', border: '1px solid #ccc', borderRadius: '8px' }}
                                    />
                                    <StyledButton type="submit" disabled={isCreatingGroup}>
                                        {isCreatingGroup ? <Loader2 className="icon-spin" /> : 'Zapisz Klasę'}
                                    </StyledButton>
                                    <StyledButton type="button" $variant="outline" onClick={() => setShowGroupForm(false)} style={{marginLeft: '1rem'}}>
                                        Anuluj
                                    </StyledButton>
                                </form>
                            )}

                            <CodeSearch>
                                <form onSubmit={handleSearchByCode}>
                                    <input
                                        type="text"
                                        placeholder="Wyszukaj ucznia po kodzie..."
                                        value={searchCode}
                                        onChange={e => setSearchCode(e.target.value)}
                                    />
                                    <StyledButton type="submit" disabled={isSearching} style={{padding: '0.5rem'}}>
                                        {isSearching ? <Loader2 className="icon-spin" size={16}/> : <Search size={16}/>}
                                    </StyledButton>
                                </form>
                            </CodeSearch>

                            <div style={{display: 'flex', flexDirection: 'column', gap: '1rem'}}>
                                {groups.length > 0 ? (
                                    groups.map(group => (
                                        <AccordionItem
                                            key={group.id}
                                            group={group}
                                            forceOpen={searchedGroupId === group.id}
                                            highlight={searchedGroupId === group.id}
                                        >
                                            <GenerateCodes
                                                key={group.id}
                                                groupId={group.id}
                                                groupName={group.name}
                                            />
                                        </AccordionItem>
                                    ))
                                ) : (
                                    <p style={{textAlign: 'center', fontStyle: 'italic', color: '#666', padding: '1rem 0'}}>
                                        {currentUser?.organization?.id ? "Brak zdefiniowanych klas. Kliknij 'Dodaj Klasę', aby utworzyć pierwszą." : "Sekcja dostępna po przypisaniu do organizacji."}
                                    </p>
                                )}
                            </div>
                        </SectionCard>
                    )}
                </ColumnsWrapper>
            </DashboardLayout>
        </PageContentWrapper>
    );
};

export default AdminDashboard;